/**
 * The entry: a Takeout export and the participant's own key. The key is
 * checked with one cheap call before anything runs and lives in memory for
 * the session only — never IndexedDB, never localStorage, never the URL.
 * Connect Gmail becomes the second way in during W3 (M3.12).
 */
import { useState } from 'react'
import type { FormEvent } from 'react'
import { DEFAULT_MODEL_ID } from '../../config.js'
import { checkKey } from '../../model/checkKey.js'
import { DEFAULT_WINDOW_DAYS, today, useAppState } from '../../store/appState.js'
import { parseAddresses } from './parseAddresses.js'
import styles from './Import.module.scss'

export function Import() {
  const setImport = useAppState((s) => s.setImport)
  const params = useAppState((s) => s.params)
  const [file, setFile] = useState<File | null>(null)
  const [addresses, setAddresses] = useState(params?.me.join(', ') ?? '')
  const [cutoff, setCutoff] = useState(params?.cutoff ?? today())
  const [key, setKey] = useState('')
  const [checking, setChecking] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault()
    setError(null)
    const me = parseAddresses(addresses)
    if (!file) return setError('Choose the .mbox file from your Takeout export.')
    if (me.length === 0) return setError('Enter the email address the export belongs to.')
    if (!/^\d{4}-\d{2}-\d{2}$/.test(cutoff)) return setError('Pick the day to reconstruct as of.')
    if (key.trim().length === 0) return setError('Paste your Anthropic API key.')
    setChecking(true)
    const model = params?.model ?? DEFAULT_MODEL_ID
    const check = await checkKey(key.trim(), model)
    setChecking(false)
    if (!check.ok) return setError(check.detail)
    setImport({
      params: { me, cutoff, window_days: DEFAULT_WINDOW_DAYS, model },
      file,
      api_key: key.trim(),
    })
  }

  return (
    <form className={styles.root} onSubmit={(e) => void submit(e)} noValidate>
      <h1 className={styles.title}>Reconstruct your ledger</h1>
      <p className={styles.lede}>
        Everything runs in this browser. Nothing leaves it except short message excerpts sent to
        Anthropic under your own key, to find and check commitments.
      </p>

      <div className={styles.field}>
        <label htmlFor="mbox">Your Takeout export</label>
        <input
          id="mbox"
          type="file"
          accept=".mbox"
          onChange={(e) => setFile(e.currentTarget.files?.[0] ?? null)}
        />
        <p className={styles.help}>
          Unzip the Takeout download and choose the <code>.mbox</code> file inside{' '}
          <code>Takeout/Mail</code>. It is read here and never uploaded.
        </p>
      </div>

      <div className={styles.field}>
        <label htmlFor="me">Your email address</label>
        <input
          id="me"
          type="text"
          inputMode="email"
          autoComplete="email"
          value={addresses}
          onChange={(e) => setAddresses(e.currentTarget.value)}
          placeholder="you@example.com"
        />
        <p className={styles.help}>
          The addresses that are you. Separate several with commas; the first is the main one.
        </p>
      </div>

      <div className={styles.field}>
        <label htmlFor="cutoff">As of</label>
        <input
          id="cutoff"
          type="date"
          value={cutoff}
          onChange={(e) => setCutoff(e.currentTarget.value)}
        />
        <p className={styles.help}>
          The ledger is reconstructed as of this day, from the {DEFAULT_WINDOW_DAYS} days before it.
        </p>
      </div>

      <div className={styles.field}>
        <label htmlFor="key">Anthropic API key</label>
        <input
          id="key"
          type="password"
          autoComplete="off"
          spellCheck={false}
          value={key}
          onChange={(e) => setKey(e.currentTarget.value)}
        />
        <p className={styles.help}>
          Used from this browser only, for this session, and never stored. Set a monthly spend cap
          on the key first (
          <a href="https://console.anthropic.com/settings/limits" target="_blank" rel="noopener">
            Anthropic console › Limits
          </a>
          ).
        </p>
      </div>

      {error && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}

      <button type="submit" className={styles.primary} disabled={checking}>
        {checking ? 'Checking the key…' : 'Check the key and continue'}
      </button>
    </form>
  )
}
